import { useRef, useState } from "react"
import { useMutation } from "convex/react"
import { toast } from "sonner"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { errorMessage } from "@/lib/errors"

export type AssetUploadPurpose =
  | "resume"
  | "avatar"
  | "company_logo"
  | "hackathon_image"
  | "outreach_attachment"
  | "job_document"

/** What consuming mutations need to finalize a claimed upload. */
export type UploadedAssetRef = {
  claimId: Id<"assetClaims">
  storageId: Id<"_storage">
}

/**
 * Generic claimed upload: validate → claim (server authorizes the purpose
 * and pins limits) → POST to the claim's URL → hand {claimId, storageId} to
 * `onUploaded`, whose mutation finalizes against real storage metadata.
 */
export function useStorageUpload(opts: {
  purpose: AssetUploadPurpose
  onUploaded: (ref: UploadedAssetRef, file: File) => Promise<unknown> | void
  /** Early client-side rejection; the server re-validates on finalize. */
  validate?: (file: File) => string | null
  successMessage?: string
}) {
  const createUploadClaim = useMutation(api.assets.createUploadClaim)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const clearInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const upload = async (file: File) => {
    const problem = opts.validate?.(file)
    if (problem) {
      toast.error(problem)
      // Clear here too, not just in the finally below: a file input fires no
      // change event when the same file is picked again, so the natural retry
      // after a size/type rejection (shrink it, save over the same path,
      // re-pick it) would otherwise do nothing at all.
      clearInput()
      return
    }
    setUploading(true)
    try {
      const claim = await createUploadClaim({ purpose: opts.purpose })
      const res = await fetch(claim.uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      })
      if (!res.ok) throw new Error("Upload failed")
      const { storageId } = (await res.json()) as { storageId: Id<"_storage"> }
      await opts.onUploaded({ claimId: claim.claimId, storageId }, file)
      if (opts.successMessage) toast.success(opts.successMessage)
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"))
    } finally {
      setUploading(false)
      clearInput()
    }
  }

  return { fileInputRef, uploading, upload }
}

/** Standard validator for image uploads (logo/avatar/poster). */
export function imageFileValidator(maxBytes: number) {
  return (file: File): string | null => {
    if (!file.type.startsWith("image/")) return "Please choose an image file"
    if (file.size > maxBytes) {
      return `Image must be under ${Math.round(maxBytes / (1024 * 1024))} MB`
    }
    return null
  }
}
